import React, { useEffect, useState, useCallback } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { CrudSection } from '../components/CrudSection';
import { MixOverview } from '../components/MixOverview';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { InlinePctCell } from '../components/InlinePctCell';
import { formatPercent, formatNumber, formatCurrency, formatEurPerDay } from '../lib/format';

export default function MixImpianti() {
  const { active, sid, loading, error, reload } = useScenario();
  const { canWriteList } = useAuth();
  const canWrite = canWriteList('client_mix');
  const [kpi, setKpi] = useState(null);

  const loadKpi = useCallback(() => {
    if (sid) api.getKpi(sid).then(setKpi).catch(() => setKpi(null));
  }, [sid]);
  useEffect(() => { loadKpi(); }, [loadKpi]);

  const reloadAll = useCallback(() => { reload(); loadKpi(); }, [reload, loadKpi]);

  if (loading) return (<div><PageHeader title="Mix Impianti" /><LoadingTable /></div>);
  if (error || !active) return (<div><PageHeader title="Mix Impianti" /><ErrorState onRetry={reload} /></div>);

  const rows = active.client_mix || [];
  const totShare = rows.reduce((s, r) => s + (r.share_pct || 0), 0);
  const dailyById = Object.fromEntries((kpi?.client_mix_details || []).map((d) => [d.id, d.daily_sales]));
  const revenueById = Object.fromEntries((kpi?.client_mix_details || []).map((d) => [d.id, d.revenue_derived]));
  const kwpById = Object.fromEntries((kpi?.client_mix_details || []).map((d) => [d.id, d.volume_kwp]));
  const columns = [
    { key: 'name', header: 'Categoria cliente', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'share_pct', header: 'Quota', align: 'right', sortValue: (r) => r.share_pct || 0, render: (r) => <InlinePctCell row={r} field="share_pct" canWrite={canWrite} min={0} testid={`mix-quota-${r.id}`} onSave={(v) => api.updateMix(sid, 'client_mix', r.id, { ...r, share_pct: v }).then(reloadAll)} /> },
    { key: 'industrial_margin_pct', header: 'Margine su ricavi', align: 'right', sortValue: (r) => r.industrial_margin_pct || 0, render: (r) => <InlinePctCell row={r} field="industrial_margin_pct" canWrite={canWrite} testid={`mix-margin-${r.id}`} onSave={(v) => api.updateMix(sid, 'client_mix', r.id, { ...r, industrial_margin_pct: v }).then(reloadAll)} /> },
    { key: 'volume_eur', header: 'Volume (€)', align: 'right', sortable: false, render: (r) => formatCurrency(revenueById[r.id]) },
    { key: 'volume_kwp', header: 'Volume (kWp)', align: 'right', sortable: false, render: (r) => kwpById[r.id] ? `${formatNumber(kwpById[r.id], 0)} kWp` : <span className="text-muted-foreground">—</span> },
    { key: 'daily', header: 'Vendita/giorno', align: 'right', sortable: false, render: (r) => formatEurPerDay(dailyById[r.id]) },
    { key: 'price_kwp', header: 'Prezzo €/kWp', align: 'right', render: (r) => r.price_kwp ? formatCurrency(r.price_kwp) : '—' },
    { key: 'made_in_italy', header: 'Made in Italy', sortable: false, render: (r) => r.made_in_italy
        ? <Badge variant="outline" className="border-[hsl(var(--success))]/40 text-[hsl(var(--success))]">Made in Italy · {formatPercent(r.made_in_italy_pct)}</Badge>
        : <span className="text-muted-foreground">—</span> },
  ];
  const fields = [
    { name: 'name', label: 'Categoria cliente', type: 'text' },
    { name: 'share_pct', label: 'Quota (%)', type: 'percent' },
    { name: 'industrial_margin_pct', label: 'Margine su ricavi (%)', type: 'percent' },
    { name: 'price_kwp', label: 'Prezzo medio (€/kWp)', type: 'number' },
    { name: 'made_in_italy', label: 'Prodotto Made in Italy', type: 'checkbox' },
    { name: 'made_in_italy_pct', label: '% della categoria realizzata con prodotto italiano', type: 'percent',
      showIf: (v) => !!v.made_in_italy, requiredIf: (v) => !!v.made_in_italy,
      help: 'Quota (%) del volume di questa categoria realizzata con prodotto Made in Italy.' },
    { name: 'note', label: 'Note', type: 'textarea' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Mix Impianti" description="Composizione clienti della Divisione Impianti" />
      {Math.abs(totShare - 1) > 0.005 && (
        <Card className="p-3 flex items-center gap-2 text-sm border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10"><AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />Le quote sommano a {formatNumber(totShare * 100, 1)}% (atteso 100%).</Card>
      )}
      {kpi && <MixOverview division="Impianti" rows={rows} kpi={kpi} assumptions={active.assumptions} sid={sid} canWrite={canWrite} onReload={reloadAll} />}
      <CrudSection rows={rows} columns={columns} fields={fields} canWrite={canWrite} addLabel="Nuova categoria"
        onAdd={(p) => api.addMix(sid, 'client_mix', p)} onUpdate={(id, p) => api.updateMix(sid, 'client_mix', id, p)} onDelete={(id) => api.deleteMix(sid, 'client_mix', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
    </div>
  );
}
